import React, { useEffect, useRef } from 'react';
import './HeroCoin3D.css';

const COIN_TEXTURE = `${process.env.PUBLIC_URL}/images/ftmx-hero-coin.png`;
const THREE_MODULE_URL = `${process.env.PUBLIC_URL}/vendor/three.module.js`;
const AUTO_ROTATE_SPEED = 0.018;
const TILT_ANGLE = 0.28;

const HeroCoin3D = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let animationFrameId;
    let disposed = false;
    let cleanupScene = () => {};

    const init = async () => {
      const THREE = await import(/* webpackIgnore: true */ THREE_MODULE_URL);
      if (disposed || !containerRef.current) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
      camera.position.set(0, 0, 4.5);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 0);
      renderer.shadowMap.enabled = true;
      renderer.domElement.className = 'hero-coin-canvas';
      container.appendChild(renderer.domElement);

      const resize = () => {
        const size = container.clientWidth || 280;
        renderer.setSize(size, size);
        camera.aspect = 1;
        camera.updateProjectionMatrix();
      };

      resize();
      window.addEventListener('resize', resize);

      scene.add(new THREE.AmbientLight(0xffffff, 0.55));

      const keyLight = new THREE.PointLight(0xfff680, 2.2);
      keyLight.position.set(4, 5, 6);
      scene.add(keyLight);

      const fillLight = new THREE.PointLight(0x7ec8ff, 1);
      fillLight.position.set(-5, 1, 3);
      scene.add(fillLight);

      const rimLight = new THREE.PointLight(0xd9a45b, 0.7);
      rimLight.position.set(1, -4, -3);
      scene.add(rimLight);

      const movingLight = new THREE.PointLight(0xffc800, 2.8, 6);
      scene.add(movingLight);

      const textureLoader = new THREE.TextureLoader();
      const coinGroup = new THREE.Group();
      scene.add(coinGroup);

      let rotationY = 0;
      let rotationX = TILT_ANGLE;
      let velocityY = 0;
      let velocityX = 0;
      let isDragging = false;
      let lastPointerX = 0;
      let lastPointerY = 0;
      let coinReady = false;

      const onPointerDown = (event) => {
        isDragging = true;
        lastPointerX = event.clientX;
        lastPointerY = event.clientY;
        velocityY = 0;
        velocityX = 0;
        renderer.domElement.style.cursor = 'grabbing';
      };

      const onPointerMove = (event) => {
        if (!isDragging) return;
        const deltaX = event.clientX - lastPointerX;
        const deltaY = event.clientY - lastPointerY;
        rotationY += deltaX * 0.013;
        rotationX += deltaY * 0.009;
        velocityY = deltaX * 0.013;
        velocityX = deltaY * 0.009;
        lastPointerX = event.clientX;
        lastPointerY = event.clientY;
      };

      const onPointerUp = () => {
        isDragging = false;
        renderer.domElement.style.cursor = 'grab';
      };

      const onTouchStart = (event) => {
        if (!event.touches[0]) return;
        isDragging = true;
        lastPointerX = event.touches[0].clientX;
        lastPointerY = event.touches[0].clientY;
        velocityY = 0;
        velocityX = 0;
      };

      const onTouchMove = (event) => {
        if (!isDragging || !event.touches[0]) return;
        const deltaX = event.touches[0].clientX - lastPointerX;
        const deltaY = event.touches[0].clientY - lastPointerY;
        rotationY += deltaX * 0.013;
        rotationX += deltaY * 0.009;
        velocityY = deltaX * 0.013;
        velocityX = deltaY * 0.009;
        lastPointerX = event.touches[0].clientX;
        lastPointerY = event.touches[0].clientY;
      };

      renderer.domElement.style.cursor = 'grab';
      renderer.domElement.addEventListener('mousedown', onPointerDown);
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('mouseup', onPointerUp);
      renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: true });
      window.addEventListener('touchend', onPointerUp);

      const disposeCoin = (coinMesh, rimMesh, materials, textures) => {
        coinMesh.geometry.dispose();
        rimMesh.geometry.dispose();
        materials.forEach((material) => material.dispose());
        textures.forEach((texture) => texture.dispose());
      };

      let cleanupCoin = () => {};

      textureLoader.load(
        COIN_TEXTURE,
        (frontTexture) => {
          if (disposed) {
            frontTexture.dispose();
            return;
          }

          frontTexture.colorSpace = THREE.SRGBColorSpace;
          frontTexture.center.set(0.5, 0.5);
          frontTexture.rotation = Math.PI / 2;

          const backTexture = frontTexture.clone();
          backTexture.rotation = Math.PI / 2;
          backTexture.repeat.set(-1, 1);
          backTexture.offset.set(0, 0);
          backTexture.needsUpdate = true;

          const edgeMaterial = new THREE.MeshStandardMaterial({
            color: 0xc8a840,
            metalness: 0.97,
            roughness: 0.12,
          });
          const frontMaterial = new THREE.MeshStandardMaterial({
            map: frontTexture,
            metalness: 0.2,
            roughness: 0.42,
          });
          const backMaterial = new THREE.MeshStandardMaterial({
            map: backTexture,
            metalness: 0.2,
            roughness: 0.42,
          });

          const coinGeometry = new THREE.CylinderGeometry(1.42, 1.42, 0.11, 64, 1, false);
          const coinMesh = new THREE.Mesh(coinGeometry, [edgeMaterial, frontMaterial, backMaterial]);
          coinMesh.rotation.x = Math.PI / 2;

          const rimGeometry = new THREE.TorusGeometry(1.42, 0.052, 16, 64);
          const rimMaterial = new THREE.MeshStandardMaterial({
            color: 0xe0b305,
            metalness: 1,
            roughness: 0.08,
          });
          const rimMesh = new THREE.Mesh(rimGeometry, rimMaterial);

          coinGroup.add(coinMesh);
          coinGroup.add(rimMesh);
          coinReady = true;

          cleanupCoin = () => {
            disposeCoin(
              coinMesh,
              rimMesh,
              [edgeMaterial, frontMaterial, backMaterial, rimMaterial],
              [frontTexture, backTexture]
            );
          };
        }
      );

      const animate = (time) => {
        animationFrameId = requestAnimationFrame(animate);
        const elapsed = time * 0.001;

        if (!isDragging) {
          velocityY *= 0.93;
          velocityX *= 0.93;
          rotationY += velocityY + AUTO_ROTATE_SPEED;
          rotationX += velocityX;
          rotationX += (TILT_ANGLE - rotationX) * 0.04;
        }

        if (coinReady) {
          coinGroup.rotation.y = rotationY;
          coinGroup.rotation.x = rotationX;
          coinGroup.position.y = Math.sin(elapsed * 1.15) * 0.14;
        }

        movingLight.position.set(
          Math.cos(elapsed * 0.75) * 3,
          Math.sin(elapsed * 0.5) * 1.8,
          Math.sin(elapsed * 0.75) * 3 + 2
        );

        renderer.render(scene, camera);
      };

      animationFrameId = requestAnimationFrame(animate);

      cleanupScene = () => {
        cancelAnimationFrame(animationFrameId);
        window.removeEventListener('resize', resize);
        renderer.domElement.removeEventListener('mousedown', onPointerDown);
        window.removeEventListener('mousemove', onPointerMove);
        window.removeEventListener('mouseup', onPointerUp);
        renderer.domElement.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onPointerUp);
        cleanupCoin();
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    };

    init().catch(() => {
      // Fail silently if Three.js cannot be loaded.
    });

    return () => {
      disposed = true;
      cleanupScene();
    };
  }, []);

  return <div ref={containerRef} className="hero-coin-stage" aria-hidden="true" />;
};

export default HeroCoin3D;
